"use client";

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { FileCheck, Plus, Package, Loader2, AlertCircle, Trash2 } from "lucide-react";
import {
  loadSubstantiveProcedures,
  addProcedureToWorkspace,
  getWorkspaceProcedures,
  deleteProceduresFromWorkspace,
} from "../../utils/substantive-procedures-utils";
import {
  getStandardProceduresForWorkspace,
  STANDARD_PROCEDURES,
} from "../../constants/standard-procedures";
import {
  PROCEDURE_MODULES,
  AGEING_ANALYSIS_PROCEDURE_PREFIX,
  COMPOSITION_ANALYSIS_PROCEDURE_PREFIX,
} from "../../constants/procedure-modules";

interface AddProcedureDialogProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceName: string;
  workspacePrefix?: string;
  area?: "Planning" | "Execution" | "ConcludingProcedures";
  onProcedureAdded: () => void;
}

type ProcedureType = "standard" | "custom" | "module" | "delete" | null;

export default function AddProcedureDialog({
  isOpen,
  onClose,
  workspaceName,
  workspacePrefix,
  area = "Execution",
  onProcedureAdded,
}: AddProcedureDialogProps) {
  const [procedureType, setProcedureType] = useState<ProcedureType>(null);
  const [availableStandardProcedures, setAvailableStandardProcedures] = useState<
    Array<{ name: string; id: string; icon: React.ComponentType<{ className?: string }> }>
  >([]);
  const [selectedStandardProcedures, setSelectedStandardProcedures] = useState<Set<string>>(new Set());
  const [customProcedureName, setCustomProcedureName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [adding, setAdding] = useState(false);
  const [availableProcedures, setAvailableProcedures] = useState<string[]>([]);
  const [selectedProceduresToDelete, setSelectedProceduresToDelete] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [selectedModule, setSelectedModule] = useState<string>("");
  const [addedModules, setAddedModules] = useState<Set<string>>(new Set());
  const [ageingModuleProcedureName, setAgeingModuleProcedureName] = useState("");
  const [compositionModuleProcedureName, setCompositionModuleProcedureName] = useState("");
  const [engagementFrf, setEngagementFrf] = useState<string | null>(null);

  useEffect(() => {
    const loadFrf = async () => {
      try {
        const frfResult = await (window as any).sharePointAPI?.getFrf?.();
        const frf = frfResult?.success ? String(frfResult.frf || "").trim() : "";
        setEngagementFrf(frf || null);
      } catch {
        setEngagementFrf(null);
      }
    };
    loadFrf();
  }, []);

  // Load available standard procedures when dialog opens
  useEffect(() => {
    if (isOpen && procedureType === "standard") {
      loadAvailableStandardProcedures();
    }
  }, [isOpen, procedureType, workspaceName, engagementFrf]);

  // Load available procedures for deletion when dialog opens
  useEffect(() => {
    if (isOpen && procedureType === "delete") {
      loadAvailableProceduresForDeletion();
    }
  }, [isOpen, procedureType, workspaceName]);

  // Load added modules when module type is selected
  useEffect(() => {
    if (isOpen && procedureType === "module") {
      const loadAddedModules = async () => {
        try {
          const procedures = await getWorkspaceProcedures(workspaceName);
          const moduleNames = PROCEDURE_MODULES.map(m => m.name);
          const added = new Set(procedures.filter(p => moduleNames.includes(p)));
          setAddedModules(added);
        } catch (err) {
          console.error("Failed to load added modules", err);
          setAddedModules(new Set());
        }
      };
      loadAddedModules();
    }
  }, [isOpen, procedureType, workspaceName]);

  const loadAvailableProceduresForDeletion = async () => {
    try {
      setLoading(true);
      setError("");
      const procedures = await getWorkspaceProcedures(workspaceName);
      setAvailableProcedures(procedures);
    } catch (err) {
      console.error("Failed to load procedures for deletion", err);
      setError("Failed to load procedures");
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableStandardProcedures = async () => {
    try {
      setLoading(true);
      setError("");

      // Get all standard procedures for this workspace
      const standardProcedures = getStandardProceduresForWorkspace(
        workspaceName,
        engagementFrf,
      );

      // Get already added procedures
      const addedProcedures = await getWorkspaceProcedures(workspaceName);

      // Filter out already added procedures
      const available = standardProcedures.filter(
        (proc) => !addedProcedures.includes(proc.name)
      );

      setAvailableStandardProcedures(available);
    } catch (err) {
      console.error("Failed to load available standard procedures", err);
      setError("Failed to load available procedures");
    } finally {
      setLoading(false);
    }
  };

  const handleAddStandardProcedure = async () => {
    if (selectedStandardProcedures.size === 0) {
      setError("Please select at least one procedure");
      return;
    }

    try {
      setAdding(true);
      setError("");

      const proceduresToAdd = Array.from(selectedStandardProcedures);
      const errors: string[] = [];
      const successfulProcedures: string[] = [];

      // Add each procedure
      for (const procedureName of proceduresToAdd) {
        try {
          const result = await addProcedureToWorkspace(
            workspaceName,
            procedureName,
            "standard"
          );

          if (result.success) {
            successfulProcedures.push(procedureName);
          } else {
            errors.push(`${procedureName}: ${result.error || "Failed to add procedure"}`);
          }
        } catch (err) {
          console.error("Failed to add standard procedure", procedureName, err);
          errors.push(`${procedureName}: Failed to add procedure`);
        }
      }

      // Show results
      if (errors.length > 0 && successfulProcedures.length > 0) {
        setError(`Added ${successfulProcedures.length} procedure(s) successfully. Failed to add ${errors.length} procedure(s):\n${errors.join("\n")}`);
        // Refresh the list to remove successfully added procedures
        onProcedureAdded();
        // Clear only the successfully added procedures from selection
        const newSet = new Set(selectedStandardProcedures);
        successfulProcedures.forEach(p => newSet.delete(p));
        setSelectedStandardProcedures(newSet);
      } else if (errors.length > 0) {
        setError(`Failed to add procedure(s):\n${errors.join("\n")}`);
      } else {
        // All succeeded
        onProcedureAdded();
        handleClose();
      }
    } catch (err) {
      console.error("Failed to add standard procedures", err);
      setError("Failed to add procedures");
    } finally {
      setAdding(false);
    }
  };

  const handleAddCustomProcedure = async () => {
    if (!customProcedureName.trim()) {
      setError("Please enter a procedure name");
      return;
    }

    // Validate procedure name (no special characters that could break JSON)
    if (!/^[a-zA-Z0-9\s\-_&]+$/.test(customProcedureName.trim())) {
      setError("Procedure name can only contain letters, numbers, spaces, hyphens, underscores, and ampersands");
      return;
    }

    try {
      setAdding(true);
      setError("");

      const result = await addProcedureToWorkspace(
        workspaceName,
        customProcedureName.trim(),
        "custom"
      );

      if (result.success) {
        onProcedureAdded();
        handleClose();
      } else {
        setError(result.error || "Failed to add custom procedure");
      }
    } catch (err) {
      console.error("Failed to add custom procedure", err);
      setError("Failed to add custom procedure");
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteProcedures = async () => {
    if (selectedProceduresToDelete.size === 0) {
      setError("Please select at least one procedure to delete");
      return;
    }

    try {
      setDeleting(true);
      setError("");

      const proceduresToDelete = Array.from(selectedProceduresToDelete);
      const result = await deleteProceduresFromWorkspace(
        workspaceName,
        proceduresToDelete
      );

      if (result.success) {
        onProcedureAdded();
        handleClose();
      } else {
        setError(result.error || "Failed to delete procedures");
      }
    } catch (err) {
      console.error("Failed to delete procedures", err);
      setError("Failed to delete procedures");
    } finally {
      setDeleting(false);
    }
  };

  const handleClose = () => {
    setProcedureType(null);
    setSelectedStandardProcedures(new Set());
    setCustomProcedureName("");
    setSelectedProceduresToDelete(new Set());
    setSelectedModule("");
    setAgeingModuleProcedureName("");
    setCompositionModuleProcedureName("");
    setAddedModules(new Set());
    setError("");
    setLoading(false);
    setAdding(false);
    setDeleting(false);
    onClose();
  };

  const renderContent = () => {
    if (!procedureType) {
      return (
        <div className="space-y-4">
          <DialogDescription className="text-white/60">
            Select the type of procedure you want to add to {workspaceName}
          </DialogDescription>
          <div className="grid grid-cols-1 gap-3">
            <Button
              variant="outline"
              className="h-auto p-4 flex flex-col items-start gap-2 border-white/10 hover:bg-white/10"
              onClick={() => setProcedureType("standard")}
            >
              <div className="flex items-center gap-2 w-full">
                <FileCheck className="h-5 w-5 text-blue-400" />
                <span className="font-semibold text-white">Standard Procedure</span>
              </div>
              <span className="text-xs text-white/60 text-left">
                Add a pre-defined standard procedure for this workspace
              </span>
            </Button>
            <Button
              variant="outline"
              className="h-auto p-4 flex flex-col items-start gap-2 border-white/10 hover:bg-white/10"
              onClick={() => setProcedureType("custom")}
            >
              <div className="flex items-center gap-2 w-full">
                <Plus className="h-5 w-5 text-green-400" />
                <span className="font-semibold text-white">Custom Procedure</span>
              </div>
              <span className="text-xs text-white/60 text-left">
                Create a new custom procedure with a fixed template
              </span>
            </Button>
            <Button
              variant="outline"
              className="h-auto p-4 flex flex-col items-start gap-2 border-white/10 hover:bg-white/10"
              onClick={() => setProcedureType("module")}
            >
              <div className="flex items-center gap-2 w-full">
                <Package className="h-5 w-5 text-purple-400" />
                <span className="font-semibold text-white">Module</span>
              </div>
              <span className="text-xs text-white/60 text-left">
                Add a reusable procedure module
              </span>
            </Button>
            <Button
              variant="outline"
              className="h-auto p-4 flex flex-col items-start gap-2 border-white/10 hover:bg-white/10"
              onClick={() => setProcedureType("delete")}
            >
              <div className="flex items-center gap-2 w-full">
                <Trash2 className="h-5 w-5 text-red-400" />
                <span className="font-semibold text-white">Delete Procedure</span>
              </div>
              <span className="text-xs text-white/60 text-left">
                Remove procedures from this workspace and move to rbin
              </span>
            </Button>
          </div>
        </div>
      );
    }

    if (procedureType === "standard") {
      return (
        <div className="space-y-4">
          <DialogDescription className="text-white/60">
            Select one or more standard procedures to add. Only procedures not already added are shown.
          </DialogDescription>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-white/60" />
            </div>
          ) : availableStandardProcedures.length === 0 ? (
            <div className="text-center py-8 text-white/60">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 text-white/40" />
              <p>All standard procedures for this workspace have already been added.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {availableStandardProcedures.map((proc) => {
                const Icon = proc.icon;
                const isSelected = selectedStandardProcedures.has(proc.name);
                return (
                  <button
                    key={proc.id}
                    onClick={() => {
                      const newSet = new Set(selectedStandardProcedures);
                      if (isSelected) {
                        newSet.delete(proc.name);
                      } else {
                        newSet.add(proc.name);
                      }
                      setSelectedStandardProcedures(newSet);
                    }}
                    className={`w-full p-3 rounded-lg border text-left transition-all ${
                      isSelected
                        ? "border-blue-500 bg-blue-500/10"
                        : "border-white/10 bg-white/5 hover:bg-white/10"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {
                          const newSet = new Set(selectedStandardProcedures);
                          if (isSelected) {
                            newSet.delete(proc.name);
                          } else {
                            newSet.add(proc.name);
                          }
                          setSelectedStandardProcedures(newSet);
                        }}
                        className="w-4 h-4 rounded border-white/20 bg-black/40 text-blue-500 focus:ring-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Icon className="h-5 w-5 text-white/60" />
                      <span className="text-white font-medium">{proc.name}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-400">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setProcedureType(null)}
              className="flex-1"
            >
              Back
            </Button>
            <Button
              onClick={handleAddStandardProcedure}
              disabled={selectedStandardProcedures.size === 0 || adding || loading}
              className="flex-1"
            >
              {adding ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                `Add Selected (${selectedStandardProcedures.size})`
              )}
            </Button>
          </div>
        </div>
      );
    }

    if (procedureType === "custom") {
      return (
        <div className="space-y-4">
          <DialogDescription className="text-white/60">
            Enter a name for your custom procedure. It will be created with a fixed template.
          </DialogDescription>

          <div className="space-y-2">
            <Label htmlFor="custom-procedure-name" className="text-white">
              Procedure Name
            </Label>
            <Input
              id="custom-procedure-name"
              value={customProcedureName}
              onChange={(e) => setCustomProcedureName(e.target.value)}
              placeholder="e.g. Detailed Testing Procedure"
              className="bg-white/5 border-white/20 text-white"
            />
            <p className="text-xs text-white/60">
              The procedure will include a question: "Document the steps performed in the procedure and how we have obtained reasonable assurance based on the procedure performed."
            </p>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-400">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setProcedureType(null)}
              className="flex-1"
            >
              Back
            </Button>
            <Button
              onClick={handleAddCustomProcedure}
              disabled={!customProcedureName.trim() || adding}
              className="flex-1"
            >
              {adding ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Procedure"
              )}
            </Button>
          </div>
        </div>
      );
    }

    if (procedureType === "module") {
      const availableModules = PROCEDURE_MODULES.filter((module) => {
        if (module.id === "ageing-analysis" || module.id === "composition-analysis") return true;
        return !addedModules.has(module.name);
      });

      const selectedModuleMeta = PROCEDURE_MODULES.find((m) => m.name === selectedModule);
      const isAgeingModule = selectedModuleMeta?.id === "ageing-analysis";
      const isCompositionModule = selectedModuleMeta?.id === "composition-analysis";

      const handleAddModule = async () => {
        if (!selectedModule) {
          setError("Please select a module");
          return;
        }

        let procedureNameToAdd = selectedModule;

        if (isAgeingModule || isCompositionModule) {
          const suffix = (isAgeingModule ? ageingModuleProcedureName : compositionModuleProcedureName).trim();
          const moduleLabel = isAgeingModule ? "Ageing Analysis" : "Composition Analysis";
          if (!suffix) {
            setError(`Please enter a procedure name for ${moduleLabel}`);
            return;
          }
          if (!/^[a-zA-Z0-9\s\-_&]+$/.test(suffix)) {
            setError(
              "Procedure name can only contain letters, numbers, spaces, hyphens, underscores, and ampersands"
            );
            return;
          }
          procedureNameToAdd = `${isAgeingModule ? AGEING_ANALYSIS_PROCEDURE_PREFIX : COMPOSITION_ANALYSIS_PROCEDURE_PREFIX}${suffix}`;
        }

        try {
          setAdding(true);
          setError("");

          const result = await addProcedureToWorkspace(
            workspaceName,
            procedureNameToAdd,
            "module"
          );

          if (result.success) {
            onProcedureAdded();
            handleClose();
          } else {
            setError(result.error || "Failed to add module");
          }
        } catch (err) {
          console.error("Failed to add module", err);
          setError("Failed to add module");
        } finally {
          setAdding(false);
        }
      };

      return (
        <div className="space-y-4">
          <DialogDescription className="text-white/60">
            Select a module to add to {workspaceName}. Modules are reusable across all workspaces.
          </DialogDescription>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-white/60" />
            </div>
          ) : availableModules.length === 0 ? (
            <div className="text-center py-8 text-white/60">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 text-white/40" />
              <p>All available modules have already been added to this workspace.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {availableModules.map((module) => {
                const Icon = module.icon;
                const isSelected = selectedModule === module.name;
                return (
                  <button
                    key={module.id}
                    onClick={() => setSelectedModule(module.name)}
                    className={`w-full p-3 rounded-lg border text-left transition-all ${
                      isSelected
                        ? "border-purple-500 bg-purple-500/10"
                        : "border-white/10 bg-white/5 hover:bg-white/10"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        checked={isSelected}
                        onChange={() => setSelectedModule(module.name)}
                        className="w-4 h-4 rounded border-white/20 bg-black/40 text-purple-500 focus:ring-purple-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Icon className="h-5 w-5 text-white/60" />
                      <div className="flex-1">
                        <span className="text-white font-medium block">{module.name}</span>
                        <span className="text-xs text-white/60">{module.description}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {isAgeingModule && (
            <div className="space-y-2">
              <Label htmlFor="ageing-module-procedure-name" className="text-white">
                Procedure name
              </Label>
              <Input
                id="ageing-module-procedure-name"
                value={ageingModuleProcedureName}
                onChange={(e) => setAgeingModuleProcedureName(e.target.value)}
                placeholder="e.g. Vendor ledger"
                className="bg-white/5 border-white/20 text-white"
              />
              <p className="text-xs text-white/60">
                Saved as &quot;Ageing Analysis - &lt;name&gt;&quot;. Each name must be unique in this
                workspace. CSV file: Execution_
                {workspacePrefix
                  ? `${workspacePrefix.replace(/-/g, "_").toUpperCase()}_`
                  : "<prefix>_"}
                &lt;name&gt;.csv
              </p>
            </div>
          )}

          {isCompositionModule && (
            <div className="space-y-2">
              <Label htmlFor="composition-module-procedure-name" className="text-white">
                Procedure name
              </Label>
              <Input
                id="composition-module-procedure-name"
                value={compositionModuleProcedureName}
                onChange={(e) => setCompositionModuleProcedureName(e.target.value)}
                placeholder="e.g. Trade payables breakdown"
                className="bg-white/5 border-white/20 text-white"
              />
              <p className="text-xs text-white/60">
                Saved as &quot;Composition Analysis - &lt;name&gt;&quot;. Each name must be unique in this
                workspace.
              </p>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-400">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setProcedureType(null)}
              className="flex-1"
            >
              Back
            </Button>
            <Button
              onClick={handleAddModule}
              disabled={
                !selectedModule ||
                adding ||
                loading ||
                (isAgeingModule && !ageingModuleProcedureName.trim()) ||
                (isCompositionModule && !compositionModuleProcedureName.trim())
              }
              className="flex-1"
            >
              {adding ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                "Add Module"
              )}
            </Button>
          </div>
        </div>
      );
    }

    if (procedureType === "delete") {
      return (
        <div className="space-y-4">
          <DialogDescription className="text-white/60">
            Select procedures to delete from {workspaceName}.
          </DialogDescription>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-white/60" />
            </div>
          ) : availableProcedures.length === 0 ? (
            <div className="text-center py-8 text-white/60">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 text-white/40" />
              <p>No procedures found for this workspace.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {availableProcedures.map((procName) => {
                const isSelected = selectedProceduresToDelete.has(procName);
                return (
                  <button
                    key={procName}
                    onClick={() => {
                      const newSet = new Set(selectedProceduresToDelete);
                      if (isSelected) {
                        newSet.delete(procName);
                      } else {
                        newSet.add(procName);
                      }
                      setSelectedProceduresToDelete(newSet);
                    }}
                    className={`w-full p-3 rounded-lg border text-left transition-all ${
                      isSelected
                        ? "border-red-500 bg-red-500/10"
                        : "border-white/10 bg-white/5 hover:bg-white/10"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {
                          const newSet = new Set(selectedProceduresToDelete);
                          if (isSelected) {
                            newSet.delete(procName);
                          } else {
                            newSet.add(procName);
                          }
                          setSelectedProceduresToDelete(newSet);
                        }}
                        className="w-4 h-4 rounded border-white/20 bg-black/40 text-red-500 focus:ring-red-500"
                      />
                      <span className="text-white font-medium">{procName}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-400">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setProcedureType(null)}
              className="flex-1"
              disabled={deleting}
            >
              Back
            </Button>
            <Button
              onClick={handleDeleteProcedures}
              disabled={selectedProceduresToDelete.size === 0 || deleting || loading}
              variant="destructive"
              className="flex-1"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Selected"
              )}
            </Button>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-2xl bg-black/90 border-white/10 text-white">
        <DialogHeader>
          <DialogTitle>Add Procedure</DialogTitle>
        </DialogHeader>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}

